import { getMomentTz } from "@dexteel/mesf-core";
import { Box, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { OverviewShiftMetrics } from "../models/types";
import { microLabelSx } from "../styles";

const moment = getMomentTz();

type Accent = "current" | "previous" | "run";

type Props = {
	title: string;
	subtitle?: string;
	metrics: OverviewShiftMetrics | null;
	accent?: Accent;
	loading?: boolean;
	serverTime?: Date | null;
};

const ShiftMetricsSkeleton = ({ accent }: { accent: Accent }) => {
	const stripe =
		accent === "current"
			? "#4CAF50"
			: accent === "previous"
				? "#90A4AE"
				: "#FF7A1A";
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				borderLeft: `4px solid ${stripe}`,
				pl: 1,
			}}
		>
			<Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
				<Skeleton variant="text" width={110} height={20} />
				<Skeleton variant="text" width={140} height={18} />
			</Box>
			<Box
				sx={{
					display: "flex",
					border: "1px solid #e0e0e0",
					borderRadius: 1,
					backgroundColor: "#fafafa",
					py: 0.6,
					mb: 0.5,
				}}
			>
				{Array.from({ length: 4 }).map((_, i) => (
					<Box key={i} sx={{ flex: 1, textAlign: "center", px: 0.5 }}>
						<Skeleton variant="text" width={50} sx={{ mx: "auto" }} />
						<Skeleton
							variant="text"
							width={40}
							height={24}
							sx={{ mx: "auto" }}
						/>
					</Box>
				))}
			</Box>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					columnGap: 1,
				}}
			>
				{Array.from({ length: 4 }).map((_, i) => (
					<Box
						key={i}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							py: 0.3,
							px: 0.5,
						}}
					>
						<Skeleton variant="text" width={70} />
						<Skeleton variant="text" width={40} />
					</Box>
				))}
			</Box>
		</Box>
	);
};

const ACCENT_COLORS: Record<Accent, { stripe: string; valueColor: string }> = {
	current: { stripe: "#4CAF50", valueColor: "#1b5e20" },
	previous: { stripe: "#90A4AE", valueColor: "#37474F" },
	run: { stripe: "#FF7A1A", valueColor: "#e65100" },
};

const formatNextStop = (
	shiftEnd: Date | null | undefined,
	serverTime: Date | null | undefined,
): string => {
	if (!shiftEnd) return "-";
	const end = moment(shiftEnd);
	const timeLabel = end.format("h:mma");
	if (!serverTime) return timeLabel;
	const minutesRemaining = Math.max(
		0,
		Math.round(end.diff(moment(serverTime), "minutes", true)),
	);
	return `${timeLabel} / ${minutesRemaining} min.`;
};

const fmt = (value: number | null | undefined, decimals = 0): string => {
	if (value === null || value === undefined || Number.isNaN(value)) return "-";
	return parseFloat(value.toFixed(decimals)).toLocaleString("en-US", {
		minimumFractionDigits: 0,
		maximumFractionDigits: decimals,
	});
};

const PrimaryStat = ({
	label,
	value,
	valueColor,
}: {
	label: string;
	value: string;
	valueColor: string;
}) => (
	<Box sx={{ textAlign: "center", flex: 1, minWidth: 0, px: 0.5 }}>
		<Typography
			variant="caption"
			sx={{
				...microLabelSx,
				fontSize: "0.65rem",
				display: "block",
				lineHeight: 1,
			}}
		>
			{label}
		</Typography>
		<Typography
			sx={{
				fontSize: "1.15rem",
				fontWeight: 700,
				color: valueColor,
				lineHeight: 1.1,
				mt: 0.15,
				fontVariantNumeric: "tabular-nums",
			}}
		>
			{value}
		</Typography>
	</Box>
);

const KeyValue = ({
	label,
	value,
	indicator,
}: {
	label: string;
	value: string;
	indicator?: "warn" | "delay" | null;
}) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "baseline",
			gap: 0.6,
			py: 0.1,
			px: 0.5,
		}}
	>
		{indicator && (
			<Box
				component="span"
				sx={{
					display: "inline-block",
					width: 6,
					height: 6,
					borderRadius: "50%",
					backgroundColor: indicator === "warn" ? "#FF9800" : "#F44336",
					flexShrink: 0,
				}}
			/>
		)}
		<Typography
			sx={{
				fontSize: "0.72rem",
				color: "#666",
				whiteSpace: "nowrap",
			}}
		>
			{label}
		</Typography>
		<Typography
			sx={{
				fontSize: "0.82rem",
				fontWeight: 700,
				color: "#222",
				ml: "auto",
				fontVariantNumeric: "tabular-nums",
			}}
		>
			{value}
		</Typography>
	</Box>
);

export const ShiftMetricsCard = ({
	title,
	subtitle,
	metrics,
	accent = "current",
	loading,
	serverTime,
}: Props) => {
	if (loading) return <ShiftMetricsSkeleton accent={accent} />;
	const c = ACCENT_COLORS[accent];
	const showNextStop = accent === "current";

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				borderLeft: `4px solid ${c.stripe}`,
				pl: 1,
			}}
		>
			{/* Header */}
			<Box
				sx={{
					display: "flex",
					alignItems: "baseline",
					justifyContent: "space-between",
					mb: 0.5,
					flexWrap: "wrap",
				}}
			>
				<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
					{title}
				</Typography>
				{subtitle && (
					<Typography variant="caption" sx={{ color: "#666", fontWeight: 500 }}>
						{subtitle}
					</Typography>
				)}
			</Box>

			{/* Primary stats row */}
			<Box
				sx={{
					display: "flex",
					alignItems: "stretch",
					border: "1px solid #e0e0e0",
					borderRadius: 1,
					backgroundColor: "#fafafa",
					py: 0.6,
					mb: 0.5,
				}}
			>
				<PrimaryStat
					label="Bars"
					value={fmt(metrics?.BarCount)}
					valueColor={c.valueColor}
				/>
				<Divider orientation="vertical" flexItem />
				<PrimaryStat
					label="Bundles"
					value={fmt(metrics?.BundleCount)}
					valueColor={c.valueColor}
				/>
				<Divider orientation="vertical" flexItem />
				<PrimaryStat
					label="Pcs / Hr"
					value={fmt(metrics?.PcsPerHr, 1)}
					valueColor={c.valueColor}
				/>
				<Divider orientation="vertical" flexItem />
				<PrimaryStat
					label="Bndl / Hr"
					value={fmt(metrics?.BndlPerHr, 2)}
					valueColor={c.valueColor}
				/>
			</Box>

			{/* Secondary stats — tight key:value pairs in two columns */}
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					columnGap: 1,
				}}
			>
				<Stack divider={<Divider flexItem />}>
					<KeyValue
						label="Avg. Weld Cycle (s)"
						value={fmt(metrics?.AvgCycleSec, 1)}
					/>
					<KeyValue
						label="Avg. Cleaning Cycle (s)"
						value={fmt(metrics?.AvgCleaningCycleSec, 1)}
					/>
					{showNextStop && (
						<KeyValue
							label="Next Stop / Min. Remaining"
							value={formatNextStop(metrics?.ShiftEnd, serverTime)}
						/>
					)}
				</Stack>
				<Stack divider={<Divider flexItem />}>
					<KeyValue
						label="Length - Too Short"
						value={fmt(metrics?.TooShort)}
						indicator={metrics?.TooShort ? "warn" : null}
					/>
					<KeyValue
						label="Length - Too Long"
						value={fmt(metrics?.TooLong)}
						indicator={metrics?.TooLong ? "delay" : null}
					/>
				</Stack>
			</Box>
		</Box>
	);
};
